import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SignatureCertificate } from '../../models/SignatureCertificate';
import { ScwsService } from '../../services/scws-service.service';
import { SignatureSessionService } from '../../services/signature-session.service';
import { CertificateValidationResponse } from '../../models/certificateValidationResponse';
import { LogService } from '../../../shared/services/log.service';
import { defer, from } from 'rxjs';
import { CertificateMessages } from '../../../shared/models/component-messages/certificate-messages';
import { ShowOrHideSpinner } from 'src/app/modules/shared/models/showOrHideSpinner';
import { ShowOrHideComponent } from 'src/app/modules/shared/models/showOrHideComponent';
import { SpinnerService } from 'src/app/modules/shared/services/spinner.service';
import { AnalysisMessages } from 'src/app/modules/shared/models/component-messages/analysis-messages';
import { ApiErrorCode } from '../../../shared/models/apiErrorCode';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-certificate',
  templateUrl: './certificate.component.html',
  styleUrls: ['./certificate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificateComponent extends ShowOrHideSpinner implements OnInit, OnDestroy, ShowOrHideComponent {

  @Input() change: string;
  public certificates: Array<SignatureCertificate> = new Array();
  public certificatesValidation: Map<string, CertificateValidationResponse>;
  public displayedColumns: string[] = ['subjectName', 'notBefore', 'notAfter', 'choose'];
  public certificateMessages = new CertificateMessages();
  public analysisMessage = new AnalysisMessages();
  public sessionCert = JSON.parse(sessionStorage.getItem('currentUsbCert'));
  public sessionSoftwareCert = JSON.parse(sessionStorage.getItem('currentSoftwareCert'));
  private envSession = sessionStorage.getItem(environment.scwsEnv);

  constructor(
    private LOG: LogService,
    private scwsService: ScwsService,
    private signatureSession: SignatureSessionService,
    private cd: ChangeDetectorRef,
    private spinnerService: SpinnerService
    ) {
      super();
  }

  public showComponent() {
    return this.isSpinnerNotInProgress(this.spinnerService);
  }

  public ngOnDestroy(): void {
    this.LOG.log('[CERTIFICATE DESTROY]');
  }

  public ngOnInit() {
    if (!this.envSession) {
      this.scwsService.restoreEnvironment(this.envSession).subscribe(() => {
        this.LOG.log('Environment is restored. ');
      }, error => {
        console.error('Environment can\'t restored : ', error);
      });
    }

    this.scwsService.getObjectsFromReaders().subscribe((arrayOfObjs) => {
      for (const objs of arrayOfObjs) {
        this.getSignatureCertificate(objs, 'usb');
      }
    });
    this.createCertificate('usb');

    this.scwsService.getSoftToken().subscribe(token => {
      from(token.getObjects()).subscribe((data: any) => {
        this.getSignatureCertificate(data, 'software');
      });
    });
  }

  public createCertificate(support: string) {
    if (!this.certificatesValidation) {
    this.certificatesValidation = new Map();
    }
    this.certificates.forEach(cer => {
      this.certificatesValidation.set(cer.ckId, new CertificateValidationResponse());
      const dateJour = new Date();
      const dateFin = new Date(cer.notAfter);
      const dateDebut = new Date(cer.notBefore);

      if (dateFin < dateJour || dateDebut > dateJour) {
        this.certificatesValidation.get(cer.ckId).valid = false;
        this.certificatesValidation.get(cer.ckId).message = ApiErrorCode.EXPIRED_CERTIFICATE;
      }
    });
    if (this.sessionCert) {
      this.openCertificate(this.sessionCert, support);
    }
    if (this.sessionSoftwareCert) {
      this.openCertificate(this.sessionSoftwareCert, support);
    }
    console.log('All readers : ', this.certificates);
    this.cd.detectChanges();
  }

  public isDisabled(element: any) {
    return !this.certificatesValidation.get(element.ckId).valid;
  }

  public openCertificate(element: any, support?: any) {
    const ckId = element.ckId;
    const currentCertificate = new SignatureCertificate();
    currentCertificate.ckId = ckId;
    currentCertificate.notAfter = element.notAfter;
    currentCertificate.notBefore = element.notBefore;
    currentCertificate.pem = element.pem;
    currentCertificate.subjectName = element.subjectName;
    currentCertificate.support = support;
    const currentScwsCert = this.signatureSession.scwsCerts.find(x => x.ckId === ckId);
    this.showSpinner(this.spinnerService);
    this.scwsService.verifyCertificate(currentCertificate.pem).subscribe(data => {
      if (data.valid) {
        this.certificatesValidation.get(ckId).valid = data.valid;
        this.signatureSession.signatureCertificate = currentCertificate;
        this.signatureSession.scwsCert = currentScwsCert;

        if (support === 'software') {
          sessionStorage.setItem('currentSoftwareCert', JSON.stringify(currentCertificate));
        } else {
          sessionStorage.setItem('currentUsbCert', JSON.stringify(currentCertificate));
        }
      } else {
        this.certificatesValidation.get(ckId).valid = data.valid;
        this.certificatesValidation.get(ckId).message = ApiErrorCode[data.message];
        this.signatureSession.signatureCertificate = null;
      }
      this.hideSpinner(this.spinnerService);
      this.cd.detectChanges();
    });
  }

  private getSignatureCertificate(data: any, support: string) {
    for (const obj of data) {
      if (obj.type === 'certificate') {
        defer(() => obj.getDetails()).subscribe((details: any) => {

          if (this.isNonRepudiationAndWIthQCStatements(details.extensions)) {
            this.signatureSession.scwsCerts.push(obj);
            const certificate = new SignatureCertificate();
            certificate.notAfter = obj._notAfter;
            certificate.notBefore = obj._notBefore;
            certificate.ckId = obj._ckId;
            certificate.subjectName = obj._subjectName;

            from(obj.getValue()).subscribe((value: any) => {
              certificate.pem = value;
            });
            this.certificates = this.certificates.concat(certificate);
            
            this.createCertificate(support);
          }
        });
      }
    }
  }

  private isNonRepudiationAndWIthQCStatements(extensions: any[]): boolean {
    let isNonRepudiation = false;
    let isWithQCStatements = false;

    for (const ext of extensions) {
      if (ext.object && 'keyUsage' === ext.object.shortName) {
        isNonRepudiation = ext.value && ext.value.includes('Non Repudiation');
      }
      if (ext.object && 'qcStatements' === ext.object.shortName) {
        isWithQCStatements = true;
      }
    }
    return isNonRepudiation && isWithQCStatements;
  }
}
